import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, Eye, Smartphone, Globe, FileCheck, Server, CreditCard, Trash2, MessageSquare } from 'lucide-react';

/**
 * Public-facing trust + security page.
 *
 * Tone: reassuring, plain English, written for a regular user rather than
 * a developer. Deliberately vague on implementation specifics (no
 * cipher names, no framework versions, no endpoint paths) so the page
 * doesn't double as a roadmap for anyone probing the app. The detail
 * level is set to "what a careful user wants to know" not "what an
 * auditor wants to verify". Links to public records (Companies House,
 * ICO) carry the verifiability so we don't need to say more.
 */

const Card = ({ icon: Icon, title, children }) => (
  <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
    <div className="w-12 h-12 rounded-xl bg-[#1F3A68]/10 flex items-center justify-center mb-4">
      <Icon size={22} className="text-[#1F3A68]" />
    </div>
    <h3 className="text-lg font-bold text-[#1F3A68] mb-2">{title}</h3>
    <div className="text-sm text-gray-700 leading-relaxed">{children}</div>
  </div>
);

const TrustPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F4F2E9] to-white">
      {/* Nav */}
      <nav className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center"><img src="/logo.png" alt="Ariv" className="h-[30px] w-auto object-contain" /></Link>
          <div className="flex items-center gap-6">
            <Link to="/help"    className="text-gray-600 hover:text-[#1F3A68] text-sm font-medium">Help Centre</Link>
            <Link to="/contact" className="text-gray-600 hover:text-[#1F3A68] text-sm font-medium">Contact</Link>
            <Link to="/login"   className="bg-[#1F3A68] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#1F3A68]/90">Sign In</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1F3A68]/10 text-[#1F3A68] text-xs font-semibold mb-4">
            <Shield size={14} /> Trust &amp; Safety
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#1F3A68] mb-4">
            Your trust matters
          </h1>
          <p className="text-lg text-gray-600 leading-relaxed">
            We know that signing up to a new app means handing over information you would
            rather keep private. Here is how we look after it, in plain English.
          </p>
        </div>
      </section>

      {/* Trust pillars */}
      <section className="pb-16">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          <Card icon={Lock} title="Your password stays yours">
            When you create a password, the app scrambles it before saving and only keeps the
            scrambled version. The scramble cannot be reversed. Nobody at Ariv, including the
            founder, can see what your actual password is.
          </Card>

          <Card icon={Server} title="Sensitive details stay locked away">
            The really private information you give us, like bank account numbers and tax
            references, is locked with a separate key the app holds. Even if someone got hold
            of our records, those parts would just look like jumbled characters without the key.
          </Card>

          <Card icon={Smartphone} title="Two step login, if you want it">
            Inside Settings you can turn on two factor authentication. After your password, the
            app then asks for a 6 digit code from an app on your phone. Anyone trying to break
            in would need both your password and your phone. Admins on Ariv are required to use
            this.
          </Card>

          <Card icon={Globe} title="Secure connection, always">
            Every page on Ariv uses an encrypted connection. That is the little padlock you see
            next to the address in your browser. Your login is kept somewhere a dodgy script
            on another website cannot reach.
          </Card>

          <Card icon={FileCheck} title="A real registered business">
            Ariv is operated by{' '}
            <strong>Brick &amp; Capital Ltd</strong>, a real company registered in England and
            Wales. You can verify us on the public{' '}
            <a href="https://find-and-update.company-information.service.gov.uk/company/16655788" target="_blank" rel="noopener noreferrer" className="text-[#1F3A68] underline">Companies House register (16655788)</a>{' '}
            and on the{' '}
            <a href="https://ico.org.uk/ESDWebPages/Entry/ZC129399" target="_blank" rel="noopener noreferrer" className="text-[#1F3A68] underline">ICO data protection register (ZC129399)</a>.
          </Card>

          <Card icon={Eye} title="Watching for problems">
            A monitoring tool quietly watches the app around the clock. When something goes
            wrong, the tool tells us straight away so we can fix it quickly rather than waiting
            for someone to report it. That is how we caught and fixed a glitch on the two
            factor login screen within minutes of it appearing.
          </Card>

          <Card icon={CreditCard} title="Payments handled by Stripe">
            All card and payout details are handled by Stripe, the same payments company that
            powers Apple, Amazon, Google, and most of the major UK shops you use. Your card
            number never touches our servers and never sits in our database.
          </Card>

          <Card icon={Shield} title="UK GDPR is the floor, not the ceiling">
            We follow the UK General Data Protection Regulation. We only collect what we need,
            we tell you what it is for, and you can ask us to send you a copy of everything we
            hold on you or delete the lot. The relevant tools are in your Account Settings.
          </Card>

          <Card icon={Trash2} title="Leave when you like">
            You can delete your Ariv account from your settings whenever you want. Once you do,
            we remove your personal data in line with the law and our privacy policy. The only
            things we have to keep for a while are the records HMRC requires for tax reporting,
            which is the case on any platform you would use.
          </Card>
        </div>
      </section>

      {/* Reassurance band */}
      <section className="py-14 bg-[#1F3A68] text-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Shield size={42} className="mx-auto mb-5 text-white/90" />
          <h2 className="text-3xl font-bold mb-4">Got a question we have not answered?</h2>
          <p className="text-white/80 mb-6 leading-relaxed">
            We would rather you ask than wonder. Our Help Centre covers the most common
            questions about trust and safety, and if it is still not clear we will answer you
            directly.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link
              to="/help"
              className="inline-flex items-center gap-2 bg-white text-[#1F3A68] px-5 py-3 rounded-lg font-semibold hover:bg-white/90"
            >
              <MessageSquare size={16} />
              Visit the Help Centre
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 border border-white/40 text-white px-5 py-3 rounded-lg font-semibold hover:bg-white/10"
            >
              Email us
            </Link>
          </div>
        </div>
      </section>

      {/* Legal links band */}
      <section className="py-10">
        <div className="max-w-4xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <Link to="/privacy"             className="text-gray-600 hover:text-[#1F3A68]">Privacy Policy</Link>
          <Link to="/cookies"             className="text-gray-600 hover:text-[#1F3A68]">Cookie Policy</Link>
          <Link to="/terms"               className="text-gray-600 hover:text-[#1F3A68]">Terms of Service</Link>
          <Link to="/account/privacy"     className="text-gray-600 hover:text-[#1F3A68]">Your data &amp; downloads</Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1F3A68]/95 text-white py-8">
        <div className="container mx-auto px-4 text-center text-white/60">
          <p>&copy; {new Date().getFullYear()} Ariv. A product of Brick &amp; Capital Ltd. All rights reserved.</p>
          <p className="text-xs mt-2 text-white/50">
            <strong>Brick &amp; Capital Ltd</strong> &middot; Registered in England and Wales, company number{' '}
            <a href="https://find-and-update.company-information.service.gov.uk/company/16655788" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">16655788</a>
            {' '}&middot; Registered office: Bowness House, 815 Old Kent Road, London SE15 1NX
            {' '}&middot; ICO data protection register{' '}
            <a href="https://ico.org.uk/ESDWebPages/Entry/ZC129399" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">ZC129399</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TrustPage;
